import React, { useEffect, useState, useRef, useMemo } from "react";
import { Box, CircularProgress, useMediaQuery, Alert } from "@mui/material";
import { PurpleTextArea } from "../Style";
import EnhancedPlayer from "./EnhancedPlayer";
import TextWithInputs from "./TextWithInputs";
import HighlightIncWordsCard from "../../Listening/HighlightIncWordsCard";
import { useNavigate } from "react-router-dom";
import { navigateToExit } from "../utils/navigation";
import { PurpleBtn, PurpleCopyPasteBtn } from "../../Common/Style";
import { Snackbar } from "@mui/joy";
import SaveExitBtn from "../SaveExitBtn";
import useTimer from "../hooks/useTimer";
import { useListeningQuestion } from "../hooks/useListeningQuestion";
import { useListeningValidation } from "../hooks/useListeningValidation";
import { useListeningSubmission } from "../hooks/useListeningSubmission";
import { useListeningClipboard } from "../hooks/useListeningClipboard";
import { countWords } from "../utils/listeningHelpers";
import { LISTENING_GUIDELINES, END_MODAL_SENTENCES } from "../constants/listeningConstants";


export default function ListeningFull({
  ExamId,
  quest,
  setStep,
  step,
  totalQuestions,
  MockTestAttemptID,
  commulative,
  setCommulative,
  incrementSSTAttemptedCount,
  questionsTime,
  startStopwatch,
  stopStopwatch,
  resetQuestionTimer,
  endTestAndTimeout,
  sstCompleted,
  setSSTCompleted,
  isOnWelcomePage,
  listeningNonSSTTime,
  resetStopwatch,
  sstAttemptedCount,
  sstCount,
  submitTriggerRef,
  getCachedQuestionData,
  getAudioStatus,
}) {
  const isLaptopTwo = useMediaQuery("(max-width:1000px)");
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState("");
  const [selectedMultipleOptions, setSelectedMultipleOptions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isAudioPlaying, setIsAudioPlaying] = useState(true);
  const [currentSentence, setCurrentSentence] = useState("");
  const { stopTimer } = useTimer();

  const cachedQuestionData = getCachedQuestionData ? getCachedQuestionData(quest) : null;
  const audioPreloadStatus = getAudioStatus ? getAudioStatus(quest) : "idle";
  const { questData, loading, setQuestData } = useListeningQuestion(
    quest,
    startStopwatch,
    cachedQuestionData
  );

  const isInitialLoading = loading && !questData;
  const isFetchingNext = loading && !!questData;

  // Reset states when question changes
  useEffect(() => {
    if (quest) {
      setValue("");
      setSelectedOptions("");
      setSelectedMultipleOptions([]);
      setSelectedAnswers([]);
      setIsAudioPlaying(true);
    }
  }, [quest]);
  
  const { isNextButtonDisabled, hasValidUserResponse } = useListeningValidation(
    quest, 
    questData, 
    selectedOptions, 
    selectedMultipleOptions,
    selectedAnswers, 
    value, 
    isAudioPlaying || loading
  );

  const {
    isSubmitted,
    setIsSubmitted,
    disabledButton,
    setDisabledButton,
    endModal,
    setEndModal,
    networkError,
    setNetworkError,
    handleNext,
  } = useListeningSubmission({
    quest,
    questData,
    value,
    selectedOptions,
    selectedMultipleOptions,
    selectedAnswers,
    questionsTime,
    commulative,
    MockTestAttemptID,
    ExamId,
    step,
    totalQuestions,
    setStep,
    setCommulative,
    incrementSSTAttemptedCount,
    resetQuestionTimer,
    endTestAndTimeout,
    sstCompleted,
    setSSTCompleted,
    listeningNonSSTTime,
    resetStopwatch,
    sstAttemptedCount,
    sstCount,
    stopStopwatch,
    stopTimer,
    submitTriggerRef,
    navigateOnErrorPath: "/Mocktest",
  });

  const {
    textAreaRef,
    alertMessage,
    showAlert,
    setShowAlert,
    handleCopy,
    handleCut,
    handlePaste,
  } = useListeningClipboard(value, setValue);

  const handleChangeMCQ = (event) => {
    const value = event.target.value;
    if (event.target.checked) {
      setSelectedMultipleOptions((prev) => [...prev, value]);
    } else {
      setSelectedMultipleOptions((prev) => prev.filter((item) => item !== value));
    }
  };
  
  const handleChange = (event) => {
    setValue(event.target.value);
  };

  const handleChangeRadio = (e) => {
    setSelectedOptions(e.target.value);
  };

  const style = {};

  useEffect(() => {
    const displayRandomText = () => {
      const randomIndex = Math.floor(Math.random() * END_MODAL_SENTENCES.length);
      setCurrentSentence(END_MODAL_SENTENCES[randomIndex]);
    };

    const intervalId = setInterval(displayRandomText, 1000);

    displayRandomText();

    return () => clearInterval(intervalId);
  }, []);

  const guidelineText = useMemo(() => LISTENING_GUIDELINES[quest?.SubCategory], [quest?.SubCategory]);
  const optionNames = useMemo(() => questData?.OptionNames || [], [questData?.OptionNames]);

  return (
    <div style={{}}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        open={networkError}
        variant={"outlined"}
        color={"danger"}
        onClose={() => {
          setNetworkError(false);
        }}
      >
        Network Error
      </Snackbar>
      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "transparent",
            boxShadow: "none",
            padding: 0,
            margin: 0,
          },
        }}
      >
        <Alert
          onClose={() => setShowAlert(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: isLaptopTwo ? "1rem 5% 2rem" : "3rem 5% 2rem",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            // height: "400px",
            width: "100%",
            borderRadius: "24px",
            position: "relative",
            overflow: "clip",
          }}
        >
          <div
            style={{
              width: "100%",
              backgroundColor: "#e2e2ea",
              height: "13px",
              position: "absolute",
            }}
          ></div>
          <div
            style={{
              width: `${(step / totalQuestions) * 100}%`,
              backgroundColor: "#996cfe",
              height: "13px",
              position: "absolute",
            }}
          ></div>
          {isInitialLoading ? (
            <div
              style={{
                position: "relative",
                minHeight: "50vh",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(255,255,255,0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                }}
              >
                <CircularProgress />
              </div>
            </div>
          ) : (
            questData && (
              <div
                style={{
                  //   width: "100%",
                  height: "100%",
                  padding: "20px",
                  marginTop: "20px",
                  opacity: isFetchingNext ? 0.5 : 1,
                  transition: "opacity 200ms ease",
                }}
              >
                <h1
                  style={{
                    fontSize: "20px",
                    fontWeight: 700,
                    lineHeight: "32px",
                    userSelect: "none",
                  }}
                  onCopy={(e) => e.preventDefault()}
                >
                  {guidelineText}
                </h1>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "20px",
                  }}
                >
                  <EnhancedPlayer
                    audioURL={
                      questData?.AudioObjects?.[0]?.url ||
                      questData?.AudioObjects?.[0]?.audio_url
                    }
                    Waiting={
                      quest?.SubCategory === "Summarize Spoken Text" ? 10 : 3
                    }
                    setDisabledButton={setDisabledButton}
                    title={quest?.SubCategory}
                    SubCategory={quest?.SubCategory}
                    setIsAudioPlaying={setIsAudioPlaying}
                    audioPreloadStatus={audioPreloadStatus}
                  />
                </div>

                {quest?.SubCategory ===
                  "Listening: Multiple Choice, Single Answer" ||
                quest?.SubCategory ===
                  "Listening: Multiple Choice, Multiple Answers" ||
                quest?.SubCategory === "Highlight Correct Summary" ||
                quest?.SubCategory === "Select Missing Word" ? (
                  <div
                    style={{
                      fontFamily: "Noto Sans",
                      fontSize: "18px",
                      fontWeight: 500,
                    }}
                  >
                    {questData?.OptionText}
                    <div>
                      {optionNames &&
                        optionNames.map((option, index) => {
                          const label = option; // Extracts 'a', 'b', 'c', etc.
                          return (
                            <div
                              key={index}
                              style={{
                                paddingTop: "15px",
                                fontSize: "17px",
                                fontWeight: 400,
                                display: "flex", // Ensures label and input are on the same line
                                alignItems: "center",
                                userSelect: "none",
                              }}
                              onCopy={(e) => e.preventDefault()}
                            >
                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  marginRight: "10px",
                                  userSelect: "none",
                                }}
                                onCopy={(e) => e.preventDefault()}
                              >
                                {quest?.SubCategory ===
                                "Listening: Multiple Choice, Multiple Answers" ? (
                                  <input
                                    type="checkbox"
                                    value={label}
                                    checked={selectedMultipleOptions.includes(label)}
                                    onChange={handleChangeMCQ}
                                    style={{
                                      marginRight: "5px",
                                      marginTop: "7px",
                                    }}
                                  />
                                ) : (
                                  <input
                                    type="radio"
                                    name="options"
                                    value={label}
                                    checked={selectedOptions === label}
                                    onChange={handleChangeRadio}
                                    style={{
                                      marginRight: "5px",
                                      marginTop: "7px",
                                    }}
                                  />
                                )}
                                {option}
                              </label>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : null}
                {quest?.SubCategory === "Fill in the Blanks" ? (
                  <div
                    style={{
                      userSelect: "none",
                    }}
                    onCopy={(e) => e.preventDefault()}
                  >
                    <TextWithInputs
                      text={questData?.QuestionStatement}
                      setAnswers={setSelectedOptions}
                    />
                  </div>
                ) : quest?.SubCategory === "Highlight Incorrect Words" ? (
                  <>
                    <div
                      style={{
                        userSelect: "none",
                      }}
                      onCopy={(e) => e.preventDefault()}
                    >
                      <HighlightIncWordsCard
                        textValue={questData?.QuestionStatement}
                        incorrectWords={questData?.OptionNames}
                        correctWords={questData?.AnswerNames}
                        setSelectedAnswers={setSelectedAnswers}
                        isSubmitted={isSubmitted}
                        setIsSubmitted={setIsSubmitted}
                      />
                    </div>
                    {/* <div style={{ position: "relative" }}>
                    <PurpleTextArea
                      placeholder="Write here..."
                      value={value}
                      rows={10}
                      onChange={handleChange}
                    />
                    <p style={{ position: "absolute", bottom: 0, right: 15 }}>
                      {value.split(" ").length - 1} Words
                    </p>
                  </div> */}
                  </>
                ) : quest?.SubCategory === "Write from Dictation" ||
                  quest?.SubCategory === "Summarize Spoken Text" ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginBottom: "0.4rem",
                        gap: "0.2rem",
                      }}
                    >
                      <PurpleCopyPasteBtn onClick={handleCopy}>
                        Copy
                      </PurpleCopyPasteBtn>
                      <PurpleCopyPasteBtn onClick={handleCut}>
                        Cut
                      </PurpleCopyPasteBtn>
                      <PurpleCopyPasteBtn onClick={handlePaste}>
                        Paste
                      </PurpleCopyPasteBtn>
                    </div>
                    <div spellCheck={false} style={{ position: "relative" }}>
                      <PurpleTextArea
                        ref={textAreaRef}
                        placeholder="Write here..."
                        value={value}
                        rows={10}
                        onChange={handleChange}
                      />
                      <p style={{ position: "absolute", bottom: 0, right: 15 }}>
                        {countWords(value)} Words
                      </p>
                    </div>
                  </>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "25px",
                  }}
                >
                  <SaveExitBtn />
                  
                  <PurpleBtn
                    onClick={() => {
                      if (!isNextButtonDisabled) {
                        handleNext();
                      }
                    }}
                    disabled={isNextButtonDisabled}
                  >
                    Next
                  </PurpleBtn>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
